import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import DisponibilitaBadge from "@/components/DisponibilitaBadge";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Sprint 4X.1 — CambioDisponibilitaModal
 *
 * Lavora SOLO sull'asse-1 (V/I/A) del modello 2-assi. Sempre disponibile
 * indipendentemente dallo stato commerciale (asse-2). Vincoli:
 *   - VIRTUAL/INCOMING/AVAILABLE liberamente selezionabili tra loro
 *   - AVAILABLE→VIRTUAL/INCOMING bloccato dal backend se vendita=consegnata
 *   - venduta richiede backend-side il permesso 'agire_su_pratica_venduta'
 */
const FISICA_LABELS = { VIRTUAL: "Virtuale", INCOMING: "In arrivo", AVAILABLE: "Disponibile" };
const ALL_AXIS1 = ["VIRTUAL", "INCOMING", "AVAILABLE"];

export default function CambioDisponibilitaModal({ open, onOpenChange, vehicle, onUpdated }) {
  // Sprint 4X: usa disponibilita_fisica come fonte verità asse-1.
  // Fallback su status legacy per compat (V/I/A identici, R/S/D → AVAILABLE).
  const currentDF = vehicle?.disponibilita_fisica
    || (["RESERVED", "SOLD", "DELIVERED"].includes(vehicle?.status) ? "AVAILABLE" : (vehicle?.status || "VIRTUAL"));
  const stato_comm = vehicle?.stato_commerciale_corrente || null;
  const targets = ALL_AXIS1.filter((s) => s !== currentDF);
  const [target, setTarget] = useState(targets[0] || "");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setTarget(targets[0] || "");
      setMotivo("");
    }
    // eslint-disable-next-line
  }, [open, currentDF]);

  if (!vehicle) return null;

  // Sprint 4X.1: blocca AVAILABLE→V/I se vendita=consegnata (backend ritorna 422 lo stesso, doppio gate UX)
  const blockedConsegnata = (currentDF === "AVAILABLE"
    && (target === "VIRTUAL" || target === "INCOMING")
    && stato_comm === "consegnata");

  // Sprint 4X.1: warning per vendita.venduta — richiede permesso agire_su_pratica_venduta
  const showWarningVenduta = stato_comm === "venduta";
  const showWarningConsegnata = stato_comm === "consegnata";

  // Indice ordine per detettare regressioni (richiedono motivo ≥10 char)
  const order = { VIRTUAL: 0, INCOMING: 1, AVAILABLE: 2 };
  const isRegression = order[target] < order[currentDF];

  async function submit() {
    if (!target) return;
    if (blockedConsegnata) {
      toast.error("Regressione bloccata: pratica consegnata. Annullare/regredire la pratica prima.");
      return;
    }
    if (isRegression && motivo.trim().length < 10) {
      toast.error("Motivo regressione richiesto (≥ 10 caratteri)");
      return;
    }
    const body = { target_status: target };
    if (isRegression) body.motivo = motivo.trim();
    try {
      setSaving(true);
      const r = await api.post(`/vehicles/${vehicle.id}/transition`, body);
      toast.success(`Disponibilità aggiornata: ${FISICA_LABELS[target]}`);
      onUpdated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento disponibilità");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="cambia-disponibilita-modal">
        <DialogHeader>
          <DialogTitle>Cambia disponibilità fisica</DialogTitle>
          <DialogDescription>
            La disponibilità fisica è indipendente dallo stato commerciale: cambiarla
            non altera la pratica di vendita.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Attuale:</span>
            <DisponibilitaBadge status={currentDF} />
            {stato_comm && (
              <span className="text-xs text-slate-500">
                · pratica: <span className="font-medium capitalize">{stato_comm}</span>
              </span>
            )}
          </div>

          {showWarningVenduta && (
            <div className="text-sm rounded-md bg-amber-50 border border-amber-300 text-amber-900 p-3"
                 data-testid="cambio-warning-venduta">
              <strong>Attenzione:</strong> questo veicolo ha una pratica <strong>venduta</strong>.
              La modifica della disponibilità fisica non altera lo stato commerciale,
              ma richiede il permesso <em>"agire su pratica venduta"</em>.
            </div>
          )}
          {showWarningConsegnata && (
            <div className="text-sm rounded-md bg-rose-50 border border-rose-300 text-rose-900 p-3"
                 data-testid="cambio-warning-consegnata">
              <strong>Pratica consegnata:</strong> la regressione AVAILABLE→VIRTUAL/INCOMING è bloccata.
              Per modificare la disponibilità fisica occorre prima regredire/annullare la pratica.
            </div>
          )}

          <div>
            <Label>Nuova disponibilità</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="nuova-disp-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {targets.length === 0 && (
                  <SelectItem value="__none" disabled>Nessuna transizione disponibile</SelectItem>
                )}
                {targets.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`nuova-disp-opt-${s}`}>
                    {FISICA_LABELS[s]}
                    {order[s] < order[currentDF] && " (regressione)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1">
              Per impegnare/vendere/consegnare la pratica usa i pulsanti del workflow
              nella scheda <strong>Vendita</strong>.
            </div>
          </div>

          {isRegression && (
            <div>
              <Label>Motivo regressione (min 10 caratteri) *</Label>
              <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder="Descrivi il motivo della regressione..." rows={3}
                data-testid="cambio-motivo-textarea" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving || !target || target === "__none" || blockedConsegnata}
            className="bg-blue-600 hover:bg-blue-700" data-testid="cambio-conferma">
            {saving ? "Salvataggio..." : "Conferma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
